/**
 * Debug-only persistence for a runtime override of the main API base URL.
 *
 * The override is stored separately from the build-time default (CONFIG.VITE_HOST_API)
 * so it can be applied on top of whatever the current build ships with, and cleanly
 * removed with "Reset to default". Read synchronously at SystemStore creation so the
 * override is in place before the first API request.
 *
 * Set/cleared from the Debug Admin Panel's "Config & API" tab.
 */

const STORAGE_KEY = 'api_base_url_override';

/**
 * Returns the persisted API base URL override, or null when none is set
 * (or when localStorage is unavailable, e.g. SSR / privacy mode).
 */
export function getApiBaseUrlOverride(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.length ? value : null;
  } catch (error) {
    console.error('Failed to read API base URL override:', error);
    return null;
  }
}

/**
 * Persists the API base URL override. Passing null (or an empty string)
 * removes the override so the app falls back to the build-time default.
 */
export function setApiBaseUrlOverrideStorage(url: string | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (url && url.length) {
      window.localStorage.setItem(STORAGE_KEY, url);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to write API base URL override:', error);
  }
}
